import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import T from 'i18n';
import {
  selectCurrentCase,
  selectFolders,
  selectBundleTabPage,
  selectSpecificParam,
  selectPermissions,
} from '../../common/selectors';
import FolderModal from './FolderModal';
import { folderModalActions } from './enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import CircularProgress from '@mui/material/CircularProgress';
import * as actions from './redux/actions';
import history from 'common/history';

class NothingSelectedPage extends Component {
  state = {
    showModal: false,
    modalAction: null,
  };

  componentDidMount() {
    const { actions } = this.props;
    // Ensure we have the latest set on first load of this screen
    actions && actions.fetchContainers && actions.fetchContainers();
  }

  openCreateBundleModal = () => {
    this.setState({ showModal: true, modalAction: folderModalActions.createBundle });
  };

  handleClose = () => this.setState({ showModal: false, modalAction: null });

  handleSave = ({ name }) => {
    const {
      actions: { addFolder, fetchContainer },
      folderContainers = [],
      currentCaseId,
      permissions = {},
    } = this.props;

    // Decide type by perms priority: trialbook -> team-bundle -> private-bundle
    const perms = (permissions && permissions.folders) || {};
    const canCreateTrialbook = !!perms.canCreateTrialbook;
    const canCreateTeamBundle = !!perms.canCreateTeamBundle;
    const canCreatePrivateBundle = !!perms.canCreatePrivateBundle;
    const type = canCreateTrialbook
      ? 'trialbook'
      : canCreateTeamBundle
        ? 'team-bundle'
        : canCreatePrivateBundle
          ? 'private-bundle'
          : 'trialbook';

    const containerCodeByType = {
      'trialbook': 'trialbooks',
      'team-bundle': 'teamBundles',
      'private-bundle': 'privateBundles',
    };
    const pathSegmentByType = {
      'trialbook': 'trialbundles',
      'team-bundle': 'teamBundles',
      'private-bundle': 'privateBundles',
    };
    const tCode = containerCodeByType[type];
    const selectedContainer = (folderContainers || []).find((c) => c.code === tCode);
    const isFirstInType = !(selectedContainer && (selectedContainer.folders?.length || 0) > 0);

    return addFolder({ name, type })
      .then((res) => {
        fetchContainer({ folder: { type } });
        if (isFirstInType && res && res.code) {
          const seg = pathSegmentByType[type] || 'trialbundles';
          history.push(`/case/${currentCaseId}/${seg}/${res.code}`);
        }
      })
      .catch((error) => {
        console.error('Error occurred while adding bundle from welcome screen', error);
      });
  };

  render() {
    const {
      fetchCasesPending,
      currentCase,
      isBundleTabPage,
      bundles,
      trialbooks,
      folderContainers = [],
      currentCaseId,
    } = this.props;

    const { showModal, modalAction } = this.state;
    const bundleContainerCodesView = [
      'trialbooks',
      'bundles',
      'teamBundles',
      'privateBundles',
      'courtBundles',
      'groupBundles',
    ];
    const hasAnyContainer = (folderContainers || []).some(
      (c) => bundleContainerCodesView.includes(c.code) && (c.folders?.length || 0) > 0,
    );

    // Show small spinner while containers or cases are loading to avoid flicker
    if (fetchCasesPending || this.props.fetchContainersPending) {
      return (
        <div className="case-nothing-selected-page">
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
            <CircularProgress size={20} />
          </div>
        </div>
      );
    }

    return (
      <div className="case-nothing-selected-page">
        {hasAnyContainer ? (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <p
              style={{
                fontSize: '1.25rem',
                marginBottom: '0.5rem',
                color: '#666',
                whiteSpace: 'pre-line',
              }}
            >
              {T.translate('case.pleaseSelectToGetGoing', {
                case: currentCase?.name || currentCase?.shortName || '',
              })}
            </p>
          </div>
        ) : (
          <div>
            <div style={{ marginTop: '2rem' }}>
              <p style={{ fontSize: '2.25rem', marginBottom: '0.5rem', color: '#666' }}>
                {T.translate('case.getStartedCreateBundle')}
              </p>

              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  backgroundColor: '#c5c5c5ff',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '2rem auto',
                  cursor: 'pointer',
                }}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  color="#ffffff"
                  size="lg"
                  onClick={this.openCreateBundleModal}
                />
              </div>
              <p style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#666' }}>
                {T.translate('case.bundlesOrganiseMessage')}
              </p>
            </div>
          </div>
        )}
        {showModal && (
          <FolderModal
            show={showModal}
            handleClose={this.handleClose}
            handleSave={this.handleSave}
            action={modalAction}
            folder={null}
            isBundleTabPage={isBundleTabPage}
            trialBooksData={trialbooks}
            bundles={bundles}
          />
        )}
      </div>
    );
  }
}

/* istanbul ignore next */
function mapStateToProps(state) {
  const {
    home: { fetchCasesPending },
  } = state;
  const {
    case: { fetchContainersPending },
  } = state;
  const {
    bundles = [],
    teamBundles = [],
    privateBundles = [],
    trialbooks = [],
    courtBundles = [],
    groupBundles = [],
  } = selectFolders(state);
  return {
    fetchCasesPending,
    fetchContainersPending,
    currentCase: selectCurrentCase(state),
    currentCaseId: selectSpecificParam(state, 'case'),
    isBundleTabPage: selectBundleTabPage(state),
    folderContainers: (state.case && state.case.containers) || [],
    permissions: selectPermissions(state),
    bundles,
    teamBundles,
    privateBundles,
    trialbooks,
    courtBundles,
    groupBundles,
  };
}
/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NothingSelectedPage);
